import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Button, AppBar, Toolbar, Typography, Box, CircularProgress,
    Snackbar, Alert, IconButton, Drawer, List, ListItem, ListItemButton,
    ListItemIcon, ListItemText, Divider, alpha, useTheme, useMediaQuery, Chip,
    ThemeProvider, CssBaseline, Fab, Zoom, useScrollTrigger, Badge
} from '@mui/material';
import { BrowserRouter, useLocation } from 'react-router-dom';
import theme from './theme';
import {
    Menu as MenuIcon,
    Close as CloseIcon,
    Dashboard as DashboardIcon,
    People as PeopleIcon,
    Payment as PaymentIcon,
    Receipt as ReceiptIcon,
    AccountBalance as ExpenseIcon,
    BarChart as ReportIcon,
    Build as ServiceIcon,
    TrendingUp as EnhancedReportIcon,
    Subscriptions as PlansIcon,
    Settings as SettingsIcon,
    Logout as LogoutIcon,
    ChevronRight as ChevronRightIcon,
    Campaign as MessageIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
    CloudQueue as UpstreamProviderIcon,
    NetworkCheck as NetworkDeviceIcon,
    AccountTree as NetworkTreeIcon,
    Map as NetworkMapIcon,
    SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import { filterNavItems } from './utils/navFilter.js';
import { AppContextProvider, useAppContext, apiService } from './context/AppContext.js';
import DashboardView from './components/DashboardView.js';
import SubscriptionsView from './components/SubscriptionsView.js';
import PaymentsView from './components/PaymentsView.js';
import ReportsView from './components/ReportsView.js';
import SettingsView from './components/SettingsView.js';
import SubscriptionPlansView from './components/SubscriptionPlansView.js';
import ReceiptsView from './components/ReceiptsView.js';
import LoginView from './components/LoginView.js';
import SetupWizardView from './components/SetupWizardView.js';
import LicenseBanner from './components/LicenseBanner.js';
import RegisterView from './components/RegisterView.js';
import VerifyEmailView from './components/VerifyEmailView.js';
import ForgotPasswordView from './components/ForgotPasswordView.js';
import PublicPaymentView from './components/PublicPaymentView.js';
import PublicTenantPayView from './components/PublicTenantPayView.js';
import ResetPasswordView from './components/ResetPasswordView.js';
import BillingView from './components/BillingView.js';
import SuperAdminView from './components/SuperAdminView.js';
import LandingView from './components/LandingView.js';
import TermsView from './components/TermsView.js';
import ServiceManagementView from './components/ServiceManagementView.js';
import FinanceView from './components/FinanceView.js';
import EnhancedReportsView from './components/EnhancedReportsView.js';
import MessagingView from './components/MessagingView.js';
import UpstreamProviderManagementView from './components/UpstreamProviderManagementView.js';
import NetworkDeviceManagementView from './components/NetworkDeviceManagementView.js';
import NetworkTreeView from './components/NetworkTreeView.js';
import OAuthConsentView from './components/OAuthConsentView.js';
import NetworkMapPage from './components/NetworkMapPage.js';
import CSAgentVoiceTest from './components/CSAgentVoiceTest.js';
import ErrorBoundary from './components/ErrorBoundary.js';

// ── Navigation config ────────────────────────────────────────────────────────
const NAV_ITEMS = [
    { key: 'dashboard',          label: 'Dashboard',          icon: <DashboardIcon />,      group: 'main',      allowedRoles: ['admin', 'finance'] },
    // 'employee'/'collector' get a read-only view here (status only, no
    // balance/actions) -- enforced inside SubscriptionsView.js, not by this
    // nav entry. A combined role like "employee,collector" stays read-only
    // too, since that check only grants the full view for admin/finance.
    { key: 'subscriptions',      label: 'Subscriptions',      icon: <PeopleIcon />,          group: 'main',      allowedRoles: ['admin', 'finance', 'cashier', 'employee', 'collector'] },
    { key: 'finance',            label: 'Finance',            icon: <ExpenseIcon />,         group: 'main',      allowedRoles: ['admin', 'finance'] },
    // Concept A/B (see docs/superpowers/specs/2026-08-12-network-enforcement-design.md):
    // only one of these is ever relevant, gated by BusinessSettings.network_mode, not roles.
    { key: 'upstream-providers',  label: 'Upstream Providers',  icon: <UpstreamProviderIcon />, group: 'main',    allowedRoles: ['admin', 'finance'], module: 'upstream_sync', visibleWhen: (bs) => bs?.network_mode === 'upstream_bridge' },
    { key: 'network-devices',    label: 'Network Devices',    icon: <NetworkDeviceIcon />,    group: 'main',    allowedRoles: ['admin', 'finance'], module: 'network' },
    // 'employee'/'collector' get the tree read-only: they need to see which
    // ONU a customer sits behind and whether it's down. The write action on
    // the page (Match Labels) is hidden from them in NetworkTreeView.js and
    // refused by admin_or_finance_required() on the endpoints behind it.
    { key: 'network-tree',       label: 'Network Tree',       icon: <NetworkTreeIcon />,      group: 'main',    allowedRoles: ['admin', 'finance', 'cashier', 'employee', 'collector'], module: 'network' },
    // Same roles as network-tree above: the map's read endpoints are also
    // network_view_required() (admin/finance/employee/collector), and writes
    // are admin_or_finance_required() -- enforced inside NetworkMapView.js
    // (canEdit) and the backend itself, not by this nav entry.
    { key: 'network-map',        label: 'Network Map',        icon: <NetworkMapIcon />,       group: 'main',    allowedRoles: ['admin', 'finance', 'cashier', 'employee', 'collector'], module: 'network' },
    { key: 'payments',           label: 'Payments',           icon: <PaymentIcon />,         group: 'main',      allowedRoles: ['admin', 'finance', 'cashier', 'collector'] },
    { key: 'receipts',           label: 'Receipts',           icon: <ReceiptIcon />,         group: 'main',      allowedRoles: ['admin', 'finance'] },
    { key: 'reports',            label: 'Reports',            icon: <ReportIcon />,          group: 'analytics', allowedRoles: ['admin'] },
    { key: 'enhanced-reports',   label: 'Enhanced Reports',   icon: <EnhancedReportIcon />,  group: 'analytics', allowedRoles: ['admin'] },
    { key: 'service',            label: 'Service Management', icon: <ServiceIcon />,         group: 'manage',    allowedRoles: ['admin', 'employee', 'technician'] },
    { key: 'subscription-plans', label: 'Subscription Plans', icon: <PlansIcon />,           group: 'manage',    allowedRoles: ['admin', 'finance'] },
    { key: 'messaging',          label: 'Messaging',          icon: <MessageIcon />,         group: 'manage',    allowedRoles: ['admin'], module: 'whatsapp' },
    { key: 'settings',           label: 'Settings',           icon: <SettingsIcon />,        group: 'manage',    allowedRoles: ['admin'] },
    { key: 'cs-agent-voice',     label: 'AI Voice Agent',     icon: <SmartToyIcon />,        group: 'manage',    allowedRoles: ['admin'], module: 'ai_cs' },
    { key: 'billing',            label: 'Billing & Plan',     icon: <PaymentIcon />,         group: 'manage',    allowedRoles: ['admin'], saasOnly: true },
];

const GROUP_LABELS = { main: 'Main', analytics: 'Analytics', manage: 'Management' };

// The app-wide default logo (frontend/public/serviceBillsLogo.png) is a static
// frontend asset, not something the API serves — resolve it relative to this
// origin even in dev, unlike tenant-uploaded logos which live behind the API.
const DEFAULT_LOGO_PATH = '/serviceBillsLogo.png';
const resolveLogoUrl = (logoUrl) => {
    if (!logoUrl) return null;
    if (logoUrl.startsWith('http') || logoUrl === DEFAULT_LOGO_PATH) return logoUrl;
    const apiBase = process.env.REACT_APP_API_URL ?? (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');
    return `${apiBase}${logoUrl}`;
};

// Legacy top-level views that now live as tabs inside Finance.
const LEGACY_FINANCE_VIEWS = ['resellers', 'suppliers', 'employees', 'expenses'];
// Maps a raw view key (+ optional ?tab= value) to { view, tab }.
export const resolveView = (rawView, rawTab) => {
    if (LEGACY_FINANCE_VIEWS.includes(rawView)) return { view: 'finance', tab: rawView };
    if (rawView === 'cash-flow') return { view: 'finance', tab: 'cash-flow' };
    return { view: rawView, tab: rawTab || null };
};

// ── Drawer width ─────────────────────────────────────────────────────────────
const DRAWER_WIDTH = 280;

// ── MainApp ──────────────────────────────────────────────────────────────────
const MainApp = ({
    customers, pagination, subscriptionPlans, businessSettings,
    refetchCustomers, refetchSubscriptionPlans, setSnackbar, setBusinessSettings,
    currentPage, setCurrentPage, itemsPerPage, setItemsPerPage,
    searchQuery, setSearchQuery, customerSortBy, setCustomerSortBy,
    customerResellerId, setCustomerResellerId,
    customerStatus, setCustomerStatus,
    customerExpiryDay, setCustomerExpiryDay
}) => {
    const { user, logout, modules, hasModule, isOnprem, systemInfo } = useAppContext();
    const [settingsTab, setSettingsTab] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const userRoles = (user?.role || '').split(',').map(r => r.trim());
    const hasRole = (role) => userRoles.includes(role);

    // Floating "back to top" button (mobile only) -- the AppBar/hamburger is
    // sticky, but on long pages scrolling back up to it can still take a
    // while, so surface a shortcut once the user has scrolled past a screen.
    const scrollTrigger = useScrollTrigger({ target: window, disableHysteresis: true, threshold: 300 });
    const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

    const getDefaultView = () => {
        if (window.location.pathname.startsWith('/billing')) return 'billing';
        const urlParams = new URLSearchParams(window.location.search);
        const viewParam = urlParams.get('view');
        if (viewParam) return resolveView(viewParam, urlParams.get('tab')).view;

        if (hasRole('admin') || hasRole('finance')) return 'dashboard';
        if (hasRole('employee') || hasRole('technician')) return 'service';
        if (hasRole('cashier')) return 'subscriptions';
        if (hasRole('collector')) return 'payments';
        return 'dashboard';
    };
    const [currentView, setCurrentViewRaw] = useState(getDefaultView());
    const [financeTab, setFinanceTab] = useState(() => {
        const p = new URLSearchParams(window.location.search);
        const v = p.get('view');
        return v ? resolveView(v, p.get('tab')).tab : null;
    });
    // Accepts legacy keys (resellers|suppliers|employees|expenses|cash-flow) too.
    const setCurrentView = (key, tab) => {
        const r = resolveView(key, tab);
        if (r.view === 'finance') setFinanceTab(r.tab);
        setCurrentViewRaw(r.view);
    };
    const [drawerOpen, setDrawerOpen] = useState(false);

    const [inboxOpenId, setInboxOpenId] = useState(() => {
        const v = new URLSearchParams(window.location.search).get('inbox');
        return v ? Number(v) : null;
    });
    const [inboxAttention, setInboxAttention] = useState(0);

    useEffect(() => {
        if (!hasRole('admin') || !hasModule('whatsapp')) return undefined;
        let cancelled = false;
        const poll = () => apiService.fetchInboxSummary()
            .then(r => { if (!cancelled) setInboxAttention(r.data.needs_attention || 0); })
            .catch(() => {});
        poll();
        const i = setInterval(poll, 20000);
        return () => { cancelled = true; clearInterval(i); };
    }, [modules]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!('serviceWorker' in navigator)) return undefined;
        const onMessage = (event) => {
            if (event.data?.type !== 'open-url') return;
            const url = new URL(event.data.url, window.location.origin);
            const view = url.searchParams.get('view');
            if (view) setCurrentView(view, url.searchParams.get('tab'));
            if (event.data.conversationId) setInboxOpenId(Number(event.data.conversationId));
            window.history.replaceState(null, '', url.pathname + url.search);
        };
        navigator.serviceWorker.addEventListener('message', onMessage);
        return () => navigator.serviceWorker.removeEventListener('message', onMessage);
    }, []);

    const navigate = (key) => {
        setCurrentView(key);
        setDrawerOpen(false);
    };

    const navItems = filterNavItems(NAV_ITEMS, { hasRole, businessSettings, hasModule, isOnprem });

    // A deep link (?view=network-tree) or push-notification target must not
    // render a page whose module is off: once modules have loaded, fall back
    // to the role's default view (not getDefaultView(), which re-reads ?view=).
    useEffect(() => {
        if (modules === null) return;
        const gated = NAV_ITEMS.find(n => n.key === currentView && n.module);
        if (gated && !hasModule(gated.module)) {
            setCurrentView(
                (hasRole('admin') || hasRole('finance')) ? 'dashboard'
                : (hasRole('employee') || hasRole('technician')) ? 'service'
                : hasRole('cashier') ? 'subscriptions'
                : hasRole('collector') ? 'payments' : 'dashboard'
            );
        }
    }, [modules, currentView]); // eslint-disable-line react-hooks/exhaustive-deps
    const currentLabel = navItems.find(n => n.key === currentView)?.label || 'Dashboard';

    // Per-tenant branding: resolve the logo (custom upload or app default) to an absolute URL.
    const logoSrc = resolveLogoUrl(businessSettings?.logo_url) || DEFAULT_LOGO_PATH;
    const brandName = businessSettings?.business_name || 'servicesBills';

    // ── Desktop horizontal nav ────────────────────────────────────────────────
    const DesktopNav = () => (
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, px: 1, py: 1, flexWrap: 'wrap', justifyContent: 'center', bgcolor: 'background.paper', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            {navItems.map(item => (
                <Button key={item.key}
                    variant={currentView === item.key ? 'contained' : 'text'}
                    startIcon={React.cloneElement(item.icon, { sx: { fontSize: '1rem' } })}
                    onClick={() => navigate(item.key)}
                    size="small"
                    sx={{
                        borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', px: 1.5, py: 0.8,
                        color: currentView === item.key ? 'primary.contrastText' : 'text.secondary',
                        bgcolor: currentView === item.key ? 'primary.main' : 'transparent',
                        '&:hover': { bgcolor: currentView === item.key ? 'primary.dark' : alpha(theme.palette.primary.main, 0.06) },
                        minWidth: 'auto',
                    }}
                >
                    {item.key === 'messaging' && inboxAttention > 0
                        ? <Badge color="error" badgeContent={inboxAttention} sx={{ '& .MuiBadge-badge': { right: -10 } }}>{item.label}</Badge>
                        : item.label}
                </Button>
            ))}
            <Button onClick={logout} startIcon={<LogoutIcon sx={{ fontSize: '1rem' }} />} size="small"
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', px: 1.5, py: 0.8, color: 'error.main', ml: 0.5, '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.06) } }}>
                Logout
            </Button>
        </Box>
    );

    // ── Mobile drawer ─────────────────────────────────────────────────────────
    const MobileDrawer = () => {
        const groups = [...new Set(navItems.map(n => n.group))];
        return (
            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                    sx: {
                        width: DRAWER_WIDTH,
                        background: 'linear-gradient(180deg, #1a1f3a 0%, #0f1429 100%)',
                        color: 'white',
                        borderRight: 'none',
                    }
                }}
            >
                {/* Drawer Header */}
                <Box sx={{ px: 2.5, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: 'white', lineHeight: 1.2 }}>
                            {brandName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            {user?.username} · {user?.role}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Nav Groups */}
                <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
                    {groups.map((group, gi) => (
                        <Box key={group}>
                            {gi > 0 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mx: 2, my: 0.5 }} />}
                            <Typography variant="overline" sx={{ px: 2.5, pt: 1.5, pb: 0.5, display: 'block', color: 'rgba(255,255,255,0.35)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em' }}>
                                {GROUP_LABELS[group]}
                            </Typography>
                            <List dense disablePadding>
                                {navItems.filter(n => n.group === group).map(item => {
                                    const active = currentView === item.key;
                                    return (
                                        <ListItem key={item.key} disablePadding sx={{ px: 1.5, mb: 0.25 }}>
                                            <ListItemButton
                                                onClick={() => navigate(item.key)}
                                                sx={{
                                                    borderRadius: '12px', py: 1.2, px: 1.5,
                                                    bgcolor: active ? alpha(theme.palette.primary.main, 0.85) : 'transparent',
                                                    '&:hover': { bgcolor: active ? alpha(theme.palette.primary.main, 0.9) : 'rgba(255,255,255,0.06)' },
                                                    transition: 'all 0.2s ease',
                                                }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 36, color: active ? 'white' : 'rgba(255,255,255,0.5)' }}>
                                                    {React.cloneElement(item.icon, { fontSize: 'small' })}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={item.key === 'messaging' && inboxAttention > 0 ? `${item.label} (${inboxAttention})` : item.label}
                                                    primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: active ? 700 : 500, color: active ? 'white' : 'rgba(255,255,255,0.75)' }}
                                                />
                                                {active && <ChevronRightIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }} />}
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Box>
                    ))}
                </Box>

                {/* Drawer Footer — Logout */}
                <Box sx={{ px: 2, py: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <ListItemButton onClick={logout} sx={{ borderRadius: '12px', py: 1.2, px: 1.5, '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.15) } }}>
                        <ListItemIcon sx={{ minWidth: 36, color: theme.palette.error.light }}>
                            <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: 600, color: theme.palette.error.light }} />
                    </ListItemButton>
                </Box>
            </Drawer>
        );
    };

    // ── renderView ────────────────────────────────────────────────────────────
    const renderView = () => {
        // Don't mount a gated page before modules load (or while the effect
        // above redirects away) -- it would fetch and flash a load error.
        const gatedView = NAV_ITEMS.find(n => n.key === currentView && n.module);
        if (gatedView && !hasModule(gatedView.module)) return null;
        switch (currentView) {
            case 'dashboard': return <DashboardView />;
            case 'finance': return <FinanceView key={financeTab || 'default'} initialTab={financeTab} />;
            case 'upstream-providers': return <UpstreamProviderManagementView />;
            case 'network-devices': return <NetworkDeviceManagementView />;
            case 'network-tree': return <NetworkTreeView />;
            case 'network-map': return <NetworkMapPage />;
            case 'subscriptions': return <SubscriptionsView customers={customers} pagination={pagination} subscriptionPlans={subscriptionPlans} businessSettings={businessSettings} refetchCustomers={refetchCustomers} setSnackbar={setSnackbar} currentPage={currentPage} setCurrentPage={setCurrentPage} itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} searchQuery={searchQuery} setSearchQuery={setSearchQuery} customerSortBy={customerSortBy} setCustomerSortBy={setCustomerSortBy} customerResellerId={customerResellerId} setCustomerResellerId={setCustomerResellerId} customerStatus={customerStatus} setCustomerStatus={setCustomerStatus} customerExpiryDay={customerExpiryDay} setCustomerExpiryDay={setCustomerExpiryDay} />;
            case 'payments': return <PaymentsView />;
            case 'receipts': return <ReceiptsView />;
            case 'reports': return <ReportsView />;
            case 'service': return <ServiceManagementView />;
            case 'enhanced-reports': return <EnhancedReportsView />;
            case 'subscription-plans': return <SubscriptionPlansView subscriptionPlans={subscriptionPlans} refetchSubscriptionPlans={refetchSubscriptionPlans} setSnackbar={setSnackbar} />;
            case 'messaging': return hasRole('admin') ? <MessagingView openConversationId={inboxOpenId} /> : <Typography>Access Denied</Typography>;
            case 'settings': return hasRole('admin') ? <SettingsView initialTab={settingsTab} businessSettings={businessSettings} setBusinessSettings={setBusinessSettings} setSnackbar={setSnackbar} /> : <Typography>Access Denied</Typography>;
            case 'cs-agent-voice': return hasRole('admin') ? <CSAgentVoiceTest /> : <Typography>Access Denied</Typography>;
            case 'billing': return hasRole('admin') ? <BillingView /> : <Typography>Access Denied</Typography>;
            default:
                if (hasRole('employee') || hasRole('technician')) return <ServiceManagementView />;
                return <SubscriptionsView customers={customers} pagination={pagination} subscriptionPlans={subscriptionPlans} businessSettings={businessSettings} refetchCustomers={refetchCustomers} setSnackbar={setSnackbar} currentPage={currentPage} setCurrentPage={setCurrentPage} itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} searchQuery={searchQuery} setSearchQuery={setSearchQuery} customerSortBy={customerSortBy} setCustomerSortBy={setCustomerSortBy} customerResellerId={customerResellerId} setCustomerResellerId={setCustomerResellerId} customerStatus={customerStatus} setCustomerStatus={setCustomerStatus} customerExpiryDay={customerExpiryDay} setCustomerExpiryDay={setCustomerExpiryDay} />;
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', overflowX: 'hidden' }}>
            {/* ── AppBar ── */}
            <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'white', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, color: 'text.primary', zIndex: theme.zIndex.drawer + 1 }}>
                <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
                    {/* Hamburger — mobile only */}
                    {isMobile && (
                        <IconButton edge="start" onClick={() => setDrawerOpen(true)} sx={{ mr: 1.5, color: 'text.primary', bgcolor: alpha(theme.palette.primary.main, 0.06), borderRadius: '10px', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) } }}>
                            <MenuIcon />
                        </IconButton>
                    )}

                    {/* Per-tenant logo + name (falls back to the servicesBills brand) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, minWidth: 0 }}>
                        {logoSrc && (
                            <Box component="img" src={logoSrc} alt=""
                                 sx={{ height: 32, width: 32, borderRadius: '8px', objectFit: 'cover' }} />
                        )}
                        <Typography variant="h6" noWrap sx={{ fontWeight: 800, color: 'primary.main', fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                            {brandName}
                        </Typography>
                    </Box>

                    {/* Current view badge — mobile */}
                    {isMobile && (
                        <Chip label={currentLabel} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 700, fontSize: '0.75rem', mr: 1 }} />
                    )}

                    {/* User info — desktop */}
                    {user && !isMobile && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            {user.username} · <strong>{user.role}</strong>
                        </Typography>
                    )}
                </Toolbar>
            </AppBar>

            {isOnprem && (
                <LicenseBanner onEnterLicense={() => { setSettingsTab('license'); navigate('settings'); }} />
            )}

            {/* ── Desktop Nav ── */}
            {!isMobile && <DesktopNav />}

            {/* ── Mobile Drawer ── */}
            {isMobile && <MobileDrawer />}

            {/* ── Page Content ── */}
            <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2, md: 3 } }}>
                {renderView()}
            </Box>

            {isOnprem && systemInfo?.app_version && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', pb: 2 }}>
                    ServiceBills v{systemInfo.app_version}
                </Typography>
            )}

            {/* ── Back to Top (mobile) ── */}
            {isMobile && (
                <Zoom in={scrollTrigger}>
                    <Fab
                        size="small"
                        color="primary"
                        aria-label="Scroll back to top"
                        onClick={scrollToTop}
                        sx={{ position: 'fixed', bottom: 24, right: 16, zIndex: theme.zIndex.tooltip }}
                    >
                        <KeyboardArrowUpIcon />
                    </Fab>
                </Zoom>
            )}
        </Box>
    );
};



// This component now decides whether to show the Login/Register screens or the MainApp
const AppContent = () => {
    const { isAuthenticated, setSnackbar, user, systemInfo, systemInfoLoaded } = useAppContext();
    const location = useLocation();
    const isSuperadmin = (user?.role || '') === 'superadmin';

    const [customers, setCustomers] = useState([]);
    const [pagination, setPagination] = useState({ pages: 1, total: 0, current_page: 1 });
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [businessSettings, setBusinessSettings] = useState(null);
    const [loading, setLoading] = useState(true); // --- FIX: Start with loading true

    // --- PAGINATION STATE (lifted from SubscriptionsView) ---
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [searchQuery, setSearchQuery] = useState('');
    const [customerSortBy, setCustomerSortBy] = useState('expiry_date');
    const [customerResellerId, setCustomerResellerId] = useState('');
    const [customerStatus, setCustomerStatus] = useState('active'); // 'active' | 'canceled' | 'all'
    const [customerExpiryDay, setCustomerExpiryDay] = useState(''); // '' = any day, else 1-31

    useEffect(() => {
        if (businessSettings) {
            if (businessSettings.business_name) {
                document.title = businessSettings.business_name;
            }
            const url = resolveLogoUrl(businessSettings.logo_url) || DEFAULT_LOGO_PATH;

            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.head.appendChild(link);
            }
            link.href = url;

            let appleLink = document.querySelector("link[rel='apple-touch-icon']");
            if (!appleLink) {
                appleLink = document.createElement('link');
                appleLink.rel = 'apple-touch-icon';
                document.head.appendChild(appleLink);
            }
            appleLink.href = url;
        }
    }, [businessSettings]);

    const userRoles = (user?.role || '').split(',').map(r => r.trim().toLowerCase());
    // Cashier needs the plan list to change a customer's plan in the edit
    // dialog (GET /api/subscription_plans is subscription_desk_required()).
    const canManagePlans = userRoles.includes('admin') || userRoles.includes('finance') || userRoles.includes('cashier');

    const refetchSubscriptionPlans = useCallback(async () => {
        if (!canManagePlans) return;
        try {
            const plansRes = await apiService.fetchSubscriptionPlans();
            setSubscriptionPlans(plansRes || []);
        } catch (error) {
            console.error("Error fetching subscription plans:", error);
            setSnackbar({ open: true, message: 'Failed to refresh subscription plans.', severity: 'error' });
        }
    }, [canManagePlans, setSnackbar]);

    // Latest Subscriptions query (page, filters, sort), read at call time so
    // refetchCustomers() always reloads exactly what's on screen -- callers
    // after an edit/renew/etc. used to pass only (page, perPage, search) and
    // silently drop the sort and reseller filter.
    const customerQueryRef = useRef(null);
    const customerFetchSeqRef = useRef(0);
    const refetchCustomers = useCallback(async () => {
        const q = customerQueryRef.current;
        if (!q) return;
        // Ignore a slower, older response landing after a newer one.
        const seq = ++customerFetchSeqRef.current;
        try {
            // Server does the status filter and the ordering; ascending, so
            // Expiry Date lists the nearest expiry first and Name/Address A-Z.
            const response = await apiService.fetchCustomers(q.page, q.perPage, q.search, q.sortBy, q.resellerId, q.status, false, q.expiryDay);
            if (seq !== customerFetchSeqRef.current) return;
            setCustomers(response.customers || []);
            setPagination({
                total: response.total,
                pages: response.pages,
                currentPage: response.current_page
            });
        } catch (error) {
            console.error("Error fetching customers:", error);
            setSnackbar({ open: true, message: 'Failed to refresh customers list.', severity: 'error' });
        }
    }, [setSnackbar]);

    // Debounced search query
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);
        return () => clearTimeout(timerId);
    }, [searchQuery]);

    customerQueryRef.current = {
        page: currentPage, perPage: itemsPerPage, search: debouncedSearchQuery,
        sortBy: customerSortBy, resellerId: customerResellerId, status: customerStatus,
        expiryDay: customerExpiryDay,
    };

    // Auto-fetch when pagination/filter state changes. This is also the
    // initial customers load -- loadInitialData below no longer fetches its
    // own (unpaginated, per_page=999) copy that raced this one.
    useEffect(() => {
        if (isAuthenticated && !isSuperadmin) {
            refetchCustomers();
        }
    }, [currentPage, itemsPerPage, debouncedSearchQuery, customerSortBy, customerResellerId, customerStatus, customerExpiryDay, isAuthenticated, isSuperadmin, refetchCustomers]);

    useEffect(() => {
        if (isAuthenticated && !isSuperadmin) {
            const loadInitialData = async () => {
                setLoading(true);
                try {
                    // --- FIX: Correctly await and destructure responses ---
                    const [plansRes, settingsRes] = await Promise.all([
                        canManagePlans ? apiService.fetchSubscriptionPlans() : Promise.resolve([]),
                        apiService.fetchBusinessSettings(),
                    ]);

                    // --- FIX: Remove the extra .data access ---
                    setSubscriptionPlans(plansRes || []);
                    if (settingsRes.data?.settings) {
                        setBusinessSettings(settingsRes.data.settings);
                    }
                } catch (error) {
                    console.error("Error loading initial data:", error);
                    setSnackbar({ open: true, message: 'Failed to load initial data. Please check the console for details.', severity: 'error' });
                } finally {
                    setLoading(false);
                }
            };
            loadInitialData();
        } else {
            setLoading(false); // If not authenticated, stop loading
        }
    }, [isAuthenticated, isSuperadmin, canManagePlans, setSnackbar]);


    // Public deep-link screens render regardless of auth (email links land here).
    if (location.pathname === '/terms') return <TermsView />;
    if (location.pathname === '/verify') return <VerifyEmailView />;
    if (location.pathname === '/reset-password') return <ResetPasswordView />;
    if (location.pathname === '/forgot-password') return <ForgotPasswordView />;
    if (location.pathname === '/pay') return <PublicPaymentView />;
    if (location.pathname === '/pay-business') return <PublicTenantPayView />;
    if (location.pathname === '/oauth-consent') return <OAuthConsentView />;

    // First-run setup wins over a stale token left from a previous install.
    if (systemInfoLoaded && systemInfo?.setup_required) {
        return <SetupWizardView />;
    }

    if (!isAuthenticated) {
        // Don't flash the SaaS landing page over an on-prem install (or vice
        // versa) while deployment info loads. If the request fails, systemInfo
        // stays null and we fall through to SaaS behaviour.
        if (!systemInfoLoaded) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (systemInfo?.deployment_mode === 'onprem') {
            return systemInfo.setup_required ? <SetupWizardView /> : <LoginView />;
        }
        if (location.pathname === '/register') return <RegisterView />;
        if (location.pathname === '/login') return <LoginView />;
        return <LandingView />; // public landing at '/' (and any other path)
    }

    // Platform operators get the cross-tenant admin console, not the tenant app.
    if (isSuperadmin) {
        return <SuperAdminView />;
    }

    // --- FIX: Show loader while loading is true, even after authentication ---
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading initial data...</Typography>
            </Box>
        );
    }

    return <MainApp
        customers={customers}
        pagination={pagination}
        subscriptionPlans={subscriptionPlans}
        businessSettings={businessSettings}
        refetchCustomers={refetchCustomers}
        refetchSubscriptionPlans={refetchSubscriptionPlans}
        setSnackbar={setSnackbar}
        setBusinessSettings={setBusinessSettings}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customerSortBy={customerSortBy}
        setCustomerSortBy={setCustomerSortBy}
        customerResellerId={customerResellerId}
        setCustomerResellerId={setCustomerResellerId}
        customerStatus={customerStatus}
        setCustomerStatus={setCustomerStatus}
        customerExpiryDay={customerExpiryDay}
        setCustomerExpiryDay={setCustomerExpiryDay}
    />;
};


// The final App component wraps everything in the context provider
function App() {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <ErrorBoundary>
                <BrowserRouter>
                    <AppContextProvider>
                        <AppContentWrapper />
                    </AppContextProvider>
                </BrowserRouter>
            </ErrorBoundary>
        </ThemeProvider>
    );
}

// A new wrapper component to handle the snackbar logic
const AppContentWrapper = () => {
    const { snackbar, setSnackbar } = useAppContext();

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <>
            <AppContent />
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default App;
